import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    SxProps,
} from '@mui/material';
import React, { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { vars } from '../../../../../Theme';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../../redux/store';
import { resetUserApprovalPassword } from '../../../../../redux/user/sliceUserProfile';
import { mdiAlertCircle, mdiAlertOutline } from '@mdi/js';
import Icon from '@mdi/react';
import Message from '../../_parts/Message';
import CtaButton from '../../_parts/CtaButton';

/** Styles for Dialog's component. */
const dialogSx: SxProps = {
    '& .MuiDialog-paper': {
        width: '600px',
    },
};

/**
 * Dialog Component that informs the users if the really want to reset their workflow password dialog.
 */
export default ({ open, handleClose }: IConfirmWorkflowPasswordDialogProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'resetWorkflowPasswordDialog' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    const resetPasswordLoading = useSelector(
        (_store: IAppReduxStore) => _store.userProfile.resetPassword.loading,
    );
    const resetPasswordError = useSelector(
        (_store: IAppReduxStore) => _store.userProfile.resetPassword.error,
    );

    const handleRemoveAdditionalDoc = () => {
        dispatch(resetUserApprovalPassword());
    };

    return (
        <Dialog open={open} onClose={handleClose} sx={dialogSx}>
            <Message
                iconWrapperSx={{ display: 'flex' }}
                wrapperSx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 12px 12px 16px',
                    margin: '24px 24px 8px 24px',
                }}
                text={t('attention')}
                color={vars.brandOrange}
                icon={mdiAlertOutline}
            />
            <DialogTitle
                sx={{
                    fontSize: '18px',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    color: resetPasswordError ? vars.errorRed : vars.font,
                }}
            >
                {resetPasswordError && (
                    <Icon path={mdiAlertCircle} size={1} color={vars.errorRed} />
                )}
                {resetPasswordError ? t('titleError') : t('title')}
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ color: resetPasswordError ? vars.errorRed : vars.font }}>
                    {resetPasswordError ? t('resetPasswordError') : t('resetPasswordText')}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, paddingBottom: '16px', marginTop: '4px' }}>
                <CtaButton
                    disabled={resetPasswordLoading}
                    variant='text'
                    onClick={handleClose}
                    text={t('cancel')}
                />
                <CtaButton
                    disabled={resetPasswordLoading}
                    variant='contained'
                    onClick={handleRemoveAdditionalDoc}
                    sx={{
                        backgroundColor: vars.errorDialogRed,
                        color: vars.white,
                        '&:hover': { backgroundColor: vars.brandPink },
                    }}
                    text={t('confirm')}
                />
            </DialogActions>
        </Dialog>
    );
};

/**
 * Interface that describes ConfirmWorkflowPasswordDialog's props.
 */
interface IConfirmWorkflowPasswordDialogProps {
    open: boolean;
    handleClose: () => void;
}
